import { useEffect, useState } from "react";
import "./ProjectModal.css";

const STATUS_OPTIONS = [
  { value: "planned",     label: "Planned",     icon: "📋", color: "#64748b" },
  { value: "in_progress", label: "In Progress", icon: "⚡", color: "#f59e0b" },
  { value: "completed",   label: "Completed",   icon: "✅", color: "#059669" },
];

const TECH_SUGGESTIONS = [
  "React", "Node.js", "Python", "TypeScript", "MongoDB", "PostgreSQL",
  "Docker", "AWS", "Next.js", "Express", "Vue", "GraphQL", "Redis", "Rust",
];

function ProjectModal({ onClose, onSave, existing = null }) {
  const isEdit = !!existing;

  const [name,        setName]        = useState("");
  const [description, setDescription] = useState("");
  const [status,      setStatus]      = useState("planned");
  const [techStack,   setTechStack]   = useState([]);   // array of strings
  const [techInput,   setTechInput]   = useState("");
  const [githubUrl,   setGithubUrl]   = useState("");
  const [liveUrl,     setLiveUrl]     = useState("");

  // Prefill on edit
  useEffect(() => {
    if (existing) {
      setName(existing.name               || "");
      setDescription(existing.description || "");
      setStatus(existing.status           || "planned");
      setTechStack(existing.techStack     || []);
      setGithubUrl(existing.githubUrl     || "");
      setLiveUrl(existing.liveUrl         || "");
    }
  }, [existing]);

  // ── Tech stack helpers ──────────────────────────────────
  const addTech = (tech) => {
    const t = tech.trim();
    if (!t || techStack.includes(t)) return;
    setTechStack((prev) => [...prev, t]);
    setTechInput("");
  };

  const removeTech = (tech) =>
    setTechStack((prev) => prev.filter((t) => t !== tech));

  const handleTechKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTech(techInput);
    }
    if (e.key === "Backspace" && !techInput && techStack.length) {
      setTechStack((prev) => prev.slice(0, -1));
    }
  };

  // ── Save ────────────────────────────────────────────────
  const handleSave = () => {
    if (!name.trim()) return;
    onSave({
      type:        "project",
      name:        name.trim(),
      description: description.trim(),
      status,
      techStack,
      githubUrl:   githubUrl.trim(),
      liveUrl:     liveUrl.trim(),
    });
  };

  const currentStatus = STATUS_OPTIONS.find((s) => s.value === status);

  return (
    <div className="pm-modal">

      {/* Header */}
      <div className="pm-header">
        <div className="pm-header-icon">🚀</div>
        <div>
          <h3>{isEdit ? "Edit Project" : "Add Project"}</h3>
          <p className="pm-header-sub">Track what you're building</p>
        </div>
        <button className="pm-close" onClick={onClose}>✕</button>
      </div>

      {/* Project Name */}
      <div className="pm-field">
        <label className="pm-label">Project Name <span className="pm-required">*</span></label>
        <input
          className="pm-input"
          placeholder="e.g. E-commerce Dashboard"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </div>

      {/* Description */}
      <div className="pm-field">
        <label className="pm-label">Description</label>
        <textarea
          className="pm-textarea"
          placeholder="What are you building? What problem does it solve?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
        />
      </div>

      {/* Status picker */}
      <div className="pm-field">
        <label className="pm-label">Status</label>
        <div className="pm-status-row">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s.value}
              className={`pm-status-btn ${status === s.value ? "pm-status-btn--active" : ""}`}
              style={status === s.value ? { borderColor: s.color, color: s.color, background: s.color + "12" } : {}}
              onClick={() => setStatus(s.value)}
              type="button"
            >
              {s.icon} {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tech Stack */}
      <div className="pm-field">
        <label className="pm-label">Tech Stack</label>

        {/* Tag display + input combined */}
        <div className="pm-tech-box">
          {techStack.map((t) => (
            <span key={t} className="pm-tech-tag">
              {t}
              <button
                className="pm-tech-remove"
                onClick={() => removeTech(t)}
                type="button"
              >×</button>
            </span>
          ))}
          <input
            className="pm-tech-input"
            placeholder={techStack.length === 0 ? "Type a tech and press Enter…" : "Add more…"}
            value={techInput}
            onChange={(e) => setTechInput(e.target.value)}
            onKeyDown={handleTechKeyDown}
          />
        </div>

        {/* Quick-add suggestions */}
        <div className="pm-suggestions">
          {TECH_SUGGESTIONS.filter(
            (s) => !techStack.includes(s) &&
                   s.toLowerCase().includes(techInput.toLowerCase())
          ).slice(0, 6).map((s) => (
            <button
              key={s}
              className="pm-suggestion-chip"
              onClick={() => addTech(s)}
              type="button"
            >
              + {s}
            </button>
          ))}
        </div>
      </div>

      {/* Links */}
      <div className="pm-links-row">
        <div className="pm-field pm-field--half">
          <label className="pm-label">GitHub URL</label>
          <div className="pm-link-input-wrap">
            <span className="pm-link-icon">⚙</span>
            <input
              className="pm-input pm-link-input"
              placeholder="github.com/user/repo"
              value={githubUrl}
              onChange={(e) => setGithubUrl(e.target.value)}
            />
          </div>
        </div>
        <div className="pm-field pm-field--half">
          <label className="pm-label">Live URL</label>
          <div className="pm-link-input-wrap">
            <span className="pm-link-icon">🌐</span>
            <input
              className="pm-input pm-link-input"
              placeholder="yourproject.com"
              value={liveUrl}
              onChange={(e) => setLiveUrl(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="pm-footer">
        {/* Status preview */}
        <span className="pm-status-preview" style={{ color: currentStatus.color }}>
          {currentStatus.icon} {currentStatus.label}
        </span>

        <div className="pm-footer-actions">
          <button className="pm-btn pm-btn--cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="pm-btn pm-btn--save"
            onClick={handleSave}
            disabled={!name.trim()}
          >
            {isEdit ? "Update Project" : "Add Project"}
          </button>
        </div>
      </div>

    </div>
  );
}

export default ProjectModal;